import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Stack from "@mui/material/Stack";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import TextField from "@mui/material/TextField";
import AddIcon from "@mui/icons-material/AddOutlined";
import { PageContainer, PageHeader } from "@/components/layout/page";
import { DataTable, type Column } from "@/components/data/data-table";
import { StatusChip } from "@/components/display/status-chip";
import { RelativeTime } from "@/components/display/relative-time";
import { EmptyState } from "@/components/feedback/states";
import { useToast } from "@/components/feedback/toaster";
import { type Workflow, parseSteps, useWorkflowMutations, useWorkflows } from "@/api/workflows";

export const Route = createFileRoute("/_app/workflows/")({
  component: WorkflowsList,
});

function WorkflowsList() {
  const navigate = useNavigate();
  const toast = useToast();
  const [showArchived, setShowArchived] = useState(false);
  const { data, isLoading, error, refetch } = useWorkflows(showArchived);
  const { create } = useWorkflowMutations();

  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  const columns: Column<Workflow>[] = [
    { id: "name", header: "Name", cell: (r) => r.name, sortValue: (r) => r.name },
    { id: "status", header: "Status", cell: (r) => <StatusChip status={r.status ?? "DRAFT"} /> },
    { id: "steps", header: "Steps", align: "right", cell: (r) => parseSteps(r.steps).length },
    { id: "updated", header: "Updated", cell: (r) => <RelativeTime value={r.updatedAt} /> },
  ];

  async function submit() {
    const wf = await create.mutateAsync({
      name: name.trim(),
      description: description.trim() || null,
      steps: [],
    });
    setCreating(false);
    setName("");
    setDescription("");
    toast.success("Workflow created");
    navigate({ to: "/workflows/$workflowId", params: { workflowId: wf.id } });
  }

  return (
    <PageContainer>
      <PageHeader
        title="Workflows"
        description="Long-running, stateful flows: chain pipelines with waits, branches, and human approvals."
        actions={
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={showArchived}
                  onChange={(e) => setShowArchived(e.target.checked)}
                />
              }
              label="Show archived"
            />
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreating(true)}>
              New workflow
            </Button>
          </Stack>
        }
      />
      <Card>
        <DataTable
          columns={columns}
          data={data?.content}
          isLoading={isLoading}
          error={error}
          onRetry={() => refetch()}
          rowKey={(r) => r.id}
          search={(r) => `${r.name} ${r.description ?? ""}`}
          searchPlaceholder="Search workflows…"
          onRowClick={(r) =>
            navigate({ to: "/workflows/$workflowId", params: { workflowId: r.id } })
          }
          empty={
            <EmptyState
              title="No workflows yet"
              description="Create a workflow to chain pipelines with waits, branches and approvals."
            />
          }
        />
      </Card>

      <Dialog open={creating} onClose={() => setCreating(false)} maxWidth="xs" fullWidth>
        <DialogTitle>New workflow</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <TextField
              size="small"
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <TextField
              size="small"
              label="Description"
              multiline
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
            <span style={{ fontSize: 13, opacity: 0.7 }}>
              You&apos;ll add steps on the next screen.
            </span>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreating(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={name.trim().length < 2 || create.isPending}
            onClick={submit}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
}
